import React, { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useOKXPerpFeed } from '../hooks/useOKXPerpFeed';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

export const HedgeChart = ({ perpPosition, optionHedge }) => {
  // Connect to LIVE OKX Perpetual Futures feed
  const perpFeed = useOKXPerpFeed();
  const [chartData, setChartData] = useState({ datasets: [] });
  const priceHistoryRef = useRef([]);
  const pnlHistoryRef = useRef([]);
  const fundingHistoryRef = useRef([]);

  console.log('🛡️ HedgeChart: Using OKX Perp Feed:', {
    markPrice: perpFeed.markPrice,
    fundingRate: perpFeed.fundingRate,
    volume24h: perpFeed.volume24h,
    openInterest: perpFeed.openInterest,
    isLive: perpFeed.isLive
  });

  const formatCurrency = (value) => {
    if (!value) return '$0';
    return `$${Number(value).toLocaleString('en-US', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  };

  const formatPercentage = (value, precision = 4) => {
    if (!value) return '0.0000%';
    return `${(Number(value) * 100).toFixed(precision)}%`;
  };

  // Calculate position P&L based on live perp price
  const calculatePnL = (currentPrice, position) => {
    if (!position || !currentPrice) return 0;
    
    const priceDiff = currentPrice - position.entry_price;
    const multiplier = position.side === 'long' ? 1 : -1;
    return priceDiff * position.size * multiplier;
  };

  // Calculate funding fee impact
  const calculateFundingImpact = (position) => {
    if (!position || !perpFeed.fundingRate) return 0;
    
    const notionalValue = position.size * perpFeed.markPrice;
    const fundingFee = notionalValue * perpFeed.fundingRate;
    return position.side === 'long' ? -fundingFee : fundingFee;
  };

  // Update chart with live perpetual data
  useEffect(() => {
    if (perpFeed.markPrice && perpPosition && perpFeed.isLive) {
      console.log('🛡️ HedgeChart: Updating with live perp data:', {
        markPrice: perpFeed.markPrice,
        fundingRate: perpFeed.fundingRate
      });
      
      const now = Date.now();
      const currentPnL = calculatePnL(perpFeed.markPrice, perpPosition);
      const fundingImpact = calculateFundingImpact(perpPosition);
      
      // Add new data points
      const newPricePoint = { x: now, y: perpFeed.markPrice };
      const newPnLPoint = { x: now, y: currentPnL };
      const newFundingPoint = { x: now, y: fundingImpact };
      
      // Keep last 100 points for performance
      priceHistoryRef.current = [...priceHistoryRef.current, newPricePoint].slice(-100);
      pnlHistoryRef.current = [...pnlHistoryRef.current, newPnLPoint].slice(-100);
      fundingHistoryRef.current = [...fundingHistoryRef.current, newFundingPoint].slice(-100);
      
      // Update chart with three datasets
      setChartData({
        datasets: [
          {
            label: 'BTC Mark Price',
            data: priceHistoryRef.current,
            borderColor: '#3b82f6', // Blue
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 2,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
            yAxisID: 'y',
          },
          {
            label: 'Position P&L',
            data: pnlHistoryRef.current,
            borderColor: currentPnL >= 0 ? '#10b981' : '#ef4444', // Green/Red
            backgroundColor: currentPnL >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.1,
            pointRadius: 0,
            yAxisID: 'y1',
          },
          {
            label: 'Funding Impact',
            data: fundingHistoryRef.current,
            borderColor: '#f59e0b', // Orange
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderWidth: 1,
            fill: false,
            tension: 0.1,
            pointRadius: 0,
            yAxisID: 'y1',
            borderDash: [5, 5], // Dashed line
          }
        ]
      });
    }
  }, [perpFeed.markPrice, perpFeed.fundingRate, perpPosition, perpFeed.isLive]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        labels: {
          color: 'rgba(255, 255, 255, 0.9)',
          usePointStyle: true,
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label;
            const value = context.parsed.y;
            
            if (label.includes('Price')) {
              return `${label}: ${formatCurrency(value)}`;
            } else if (label.includes('Funding')) {
              return `${label}: ${formatCurrency(value)}`;
            } else {
              return `${label}: ${formatCurrency(value)}`;
            }
          }
        }
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'minute',
          displayFormats: {
            minute: 'HH:mm'
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)'
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          callback: function(value) {
            return formatCurrency(value);
          },
          padding: 20,
          maxTicksLimit: 6,
          font: {
            size: 10
          }
        },
        title: {
          display: true,
          text: 'BTC Price',
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 11,
            weight: 'bold'
          },
          padding: {
            top: 0,
            bottom: 12
          }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          callback: function(value) {
            return formatCurrency(value);
          },
          padding: 20,
          maxTicksLimit: 6,
          font: {
            size: 10
          }
        },
        title: {
          display: true,
          text: 'P&L / Funding',
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 11,
            weight: 'bold'
          },
          padding: {
            top: 0,
            bottom: 12
          }
        }
      },
    },
  };

  return (
    <div className="bg-atticus-navy-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-white font-bold">Live Perpetual Futures Analysis</h4>
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${perpFeed.isLive ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></div>
          <span className="text-sm text-gray-400">
            {perpFeed.isLive ? 'Live Perp Data' : 'Offline'}
          </span>
        </div>
      </div>
      
      <div className="h-64 mb-4">
        <Line data={chartData} options={chartOptions} />
      </div>
      
      {/* Perpetual Futures Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div className="text-center">
          <div className="text-gray-400">Mark Price</div>
          <div className="text-white font-bold">{formatCurrency(perpFeed.markPrice)}</div>
        </div>
        
        <div className="text-center">
          <div className="text-gray-400">Funding Rate</div>
          <div className={`font-bold ${perpFeed.fundingRate >= 0 ? 'text-red-400' : 'text-green-400'}`}>
            {formatPercentage(perpFeed.fundingRate)}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-gray-400">24h Volume</div>
          <div className="text-white font-bold">
            {perpFeed.volume24h ? `${(perpFeed.volume24h / 1000).toFixed(1)}K` : '0'}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-gray-400">Open Interest</div>
          <div className="text-white font-bold">
            {perpFeed.openInterest ? `${(perpFeed.openInterest / 1000).toFixed(1)}K` : '0'}
          </div>
        </div>
      </div>
      
      {/* Position-specific data */}
      {perpPosition && (
        <div className="mt-4 pt-4 border-t border-gray-600 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-400">Position P&L</div>
            <div className={`font-bold ${calculatePnL(perpFeed.markPrice, perpPosition) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(calculatePnL(perpFeed.markPrice, perpPosition))}
            </div>
          </div>
          
          <div>
            <div className="text-gray-400">Next Funding</div>
            <div className="text-white font-bold">
              {perpFeed.getTimeToNextFunding()}
            </div>
          </div>
          
          <div>
            <div className="text-gray-400">Funding Impact</div>
            <div className={`font-bold ${calculateFundingImpact(perpPosition) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(calculateFundingImpact(perpPosition))}
            </div>
          </div>
          
          <div>
            <div className="text-gray-400">Spread</div>
            <div className="text-white font-bold">
              {formatCurrency(perpFeed.spread)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
